import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id", "data-focus", "data-hover", "aria-disabled", "disabled", "indeterminate", "data-indeterminate", "data-disabled", "aria-checked", "data-checked", "name", "aria-describedby", "aria-labelledby"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("svg", {
  class: "size-4 stroke-[--checkbox-check] opacity-0 group-data-[checked]:opacity-100 sm:h-3.5 sm:w-3.5",
  viewBox: "0 0 14 14",
  fill: "none"
}, [
  /*#__PURE__*/_createElementVNode("path", {
    class: "opacity-100 group-data-[indeterminate]:opacity-0",
    d: "M3 8L6 11L11 3.5",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round"
  }),
  /*#__PURE__*/_createElementVNode("path", {
    class: "opacity-0 group-data-[indeterminate]:opacity-100",
    d: "M3 7H11",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round"
  })
], -1)
const _hoisted_3 = [
  _hoisted_2
]

import {
		clsx,
	} from "clsx"

	import {
		computed,
		inject,
		nextTick,
		ref,
	} from "vue"

	
export default /*#__PURE__*/_defineComponent({
  __name: 'VCheckbox',
  props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		color: {
			type: String,
			default: "dark/zinc",
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		indeterminate: {
			type: Boolean,
			default: false,
		},
		name: {
			type: String,
			default: "",
		},
	},
  emits: [
		"update:modelValue",
	],
  setup(__props, { emit: __emit }) {

	const props = __props
	function onKeydown(event: any) {
		if (event.key === " ") {
			event.preventDefault()
			onInput()
		}
	}

	const fieldDisabled = inject("fieldDisabled", computed(() => false))
	const fieldsetDisabled = inject("fieldsetDisabled", computed(() => false))
	const checkboxDisabled = computed(() => {
		return fieldDisabled.value || fieldsetDisabled.value || props.disabled
	})

	const controlId: string = inject("controlId", null) || ""
	const labelId: string = inject("labelId", null) || ""

	const emit = __emit

	const onInput = async () => {
		if (checkboxDisabled.value) return
		emit("update:modelValue", !props.modelValue)
		await nextTick()
		return
	}

	const base = [
		// Basic layout
		"relative isolate flex size-[1.125rem] items-center justify-center rounded-[0.3125rem] sm:size-4",
		// Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
		"before:absolute before:inset-0 before:-z-10 before:rounded-[calc(0.3125rem-1px)] before:bg-white before:shadow",
		// Background color when checked
		"before:group-data-[checked]:bg-[--checkbox-checked-bg]",
		// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
		"dark:before:hidden",
		// Background color applied to control in dark mode
		"dark:bg-white/5 dark:group-data-[checked]:bg-[--checkbox-checked-bg]",
		// Border
		"border border-zinc-950/15 group-data-[checked]:border-transparent group-data-[checked]:group-data-[hover]:border-transparent group-data-[hover]:border-zinc-950/30 group-data-[checked]:bg-[--checkbox-checked-border]",
		"dark:border-white/15 dark:group-data-[checked]:border-white/5 dark:group-data-[checked]:group-data-[hover]:border-white/5 dark:group-data-[hover]:border-white/30",
		// Inner highlight shadow
		"after:absolute after:inset-0 after:rounded-[calc(0.3125rem-1px)] after:shadow-[inset_0_1px_theme(colors.white/15%)]",
		"dark:after:-inset-px dark:after:hidden dark:after:rounded-[0.3125rem] dark:group-data-[checked]:after:block",
		// Focus ring
		"group-data-[focus]:outline group-data-[focus]:outline-2 group-data-[focus]:outline-offset-2 group-data-[focus]:outline-teal-500",
		// Disabled state
		"group-data-[disabled]:opacity-50",
		"group-data-[disabled]:border-zinc-950/25 group-data-[disabled]:bg-zinc-950/5 group-data-[disabled]:[--checkbox-check:theme(colors.zinc.950/50%)] group-data-[disabled]:before:bg-transparent",
		"dark:group-data-[disabled]:border-white/20 dark:group-data-[disabled]:bg-white/[2.5%] dark:group-data-[disabled]:[--checkbox-check:theme(colors.white/50%)] dark:group-data-[disabled]:group-data-[checked]:after:hidden",
		// Forced colors mode
		"forced-colors:[--checkbox-check:HighlightText] forced-colors:[--checkbox-checked-bg:Highlight] forced-colors:group-data-[disabled]:[--checkbox-check:Highlight]",
		"dark:forced-colors:[--checkbox-check:HighlightText] dark:forced-colors:[--checkbox-checked-bg:Highlight] dark:forced-colors:group-data-[disabled]:[--checkbox-check:Highlight]",
	]

	const colors: { [ k: string ]: any } = {
		"dark/zinc": [
			"[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.zinc.900)] [--checkbox-checked-border:theme(colors.zinc.950/90%)]",
			"dark:[--checkbox-checked-bg:theme(colors.zinc.600)]",
		],
		"dark/white": [
			"[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.zinc.900)] [--checkbox-checked-border:theme(colors.zinc.950/90%)]",
			"dark:[--checkbox-check:theme(colors.zinc.900)] dark:[--checkbox-checked-bg:theme(colors.white)] dark:[--checkbox-checked-border:theme(colors.zinc.950/15%)]",
		],
		white: "[--checkbox-check:theme(colors.zinc.900)] [--checkbox-checked-bg:theme(colors.white)] [--checkbox-checked-border:theme(colors.zinc.950/15%)]",
		dark: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.zinc.900)] [--checkbox-checked-border:theme(colors.zinc.950/90%)]",
		zinc: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.zinc.600)] [--checkbox-checked-border:theme(colors.zinc.700/90%)]",
		red: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.red.600)] [--checkbox-checked-border:theme(colors.red.700/90%)]",
		orange: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.orange.500)] [--checkbox-checked-border:theme(colors.orange.600/90%)]",
		amber: "[--checkbox-check:theme(colors.amber.950)] [--checkbox-checked-bg:theme(colors.amber.400)] [--checkbox-checked-border:theme(colors.amber.500/80%)]",
		yellow: "[--checkbox-check:theme(colors.yellow.950)] [--checkbox-checked-bg:theme(colors.yellow.300)] [--checkbox-checked-border:theme(colors.yellow.400/80%)]",
		lime: "[--checkbox-check:theme(colors.lime.950)] [--checkbox-checked-bg:theme(colors.lime.300)] [--checkbox-checked-border:theme(colors.lime.400/80%)]",
		green: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.green.600)] [--checkbox-checked-border:theme(colors.green.700/90%)]",
		emerald: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.emerald.600)] [--checkbox-checked-border:theme(colors.emerald.700/90%)]",
		teal: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.teal.600)] [--checkbox-checked-border:theme(colors.teal.700/90%)]",
		cyan: "[--checkbox-check:theme(colors.cyan.950)] [--checkbox-checked-bg:theme(colors.cyan.300)] [--checkbox-checked-border:theme(colors.cyan.400/80%)]",
		sky: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.sky.500)] [--checkbox-checked-border:theme(colors.sky.600/80%)]",
		blue: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.blue.600)] [--checkbox-checked-border:theme(colors.blue.700/90%)]",
		indigo: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.indigo.500)] [--checkbox-checked-border:theme(colors.indigo.600/90%)]",
		violet: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.violet.500)] [--checkbox-checked-border:theme(colors.violet.600/90%)]",
		purple: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.purple.500)] [--checkbox-checked-border:theme(colors.purple.600/90%)]",
		fuchsia: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.fuchsia.500)] [--checkbox-checked-border:theme(colors.fuchsia.600/90%)]",
		pink: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.pink.500)] [--checkbox-checked-border:theme(colors.pink.600/90%)]",
		rose: "[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.rose.500)] [--checkbox-checked-border:theme(colors.rose.600/90%)]",
	}

	const focused = ref<boolean>(false)
	const hovered = ref<boolean>(false)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    id: _unref(controlId) ?? undefined,
    "data-focus": focused.value ? true : null,
    "data-hover": hovered.value ? true : null,
    "aria-disabled": checkboxDisabled.value ? 'true' : undefined,
    disabled: checkboxDisabled.value,
    indeterminate: __props.indeterminate ? true : null,
    "data-indeterminate": __props.indeterminate ? true : null,
    "data-disabled": checkboxDisabled.value ? true : null,
    tabindex: "0",
    "aria-checked": __props.indeterminate ? 'mixed' : __props.modelValue ? 'true' : 'false',
    "data-checked": __props.modelValue ? true : null,
    "data-slot": "control",
    role: "checkbox",
    class: "group inline-flex focus:outline-none",
    name: __props.name ? __props.name : null,
    "aria-describedby": _unref(labelId),
    "aria-labelledby": _unref(labelId),
    onKeydown: onKeydown,
    onClick: onInput,
    onFocusin: _cache[0] || (_cache[0] = ($event: any) => (focused.value = true)),
    onFocusout: _cache[1] || (_cache[1] = ($event: any) => (focused.value = false)),
    onMouseover: _cache[2] || (_cache[2] = ($event: any) => (hovered.value = true)),
    onMouseout: _cache[3] || (_cache[3] = ($event: any) => (hovered.value = false))
  }, [
    _createElementVNode("span", {
      class: _normalizeClass(_unref(clsx)([base, colors[props.color]]))
    }, _hoisted_3, 2)
  ], 40, _hoisted_1))
}
}

})